<template>
  <div class="main-area" id="productInfoSection">
    <PageTop title="Ürün Bilgileri" />
    <div class="input-area">
      <div class="input-with-info d-flex flex-column">
        <Input
          id="productName"
          v-model:value="productName"
          :isError="!isProductNameValid"
          placeholder="Ürün Adı*"
          helperText="Marka adı ve belirtmek istediğiniz özelliklerle ürün adını oluşturabilirsiniz."
          type="text"
          @change="validateProductName"
          @focus="isProductNameValid = true"
        />
        <div v-if="!isProductNameValid" class="basic-input-icon">
          <TooltipPlugin :tooltipText="inputsErrorText.productName">
            <template #tooltip-btn>
              <img src="@/assets/images/icons/alert-circle.svg" />
            </template>
          </TooltipPlugin>
        </div>
      </div>
      <div class="input-with-info d-flex flex-column">
        <DropdownSelectable
          :dropItems="categories"
          label="Ürün Kategorisi*"
          notFoundText="Kategori bulunamadı."
          @selectedRadioItem="selectCategory"
          ref="dropdownSelectableCategoriesRef"
          isComboBox
          customInputLabel="Yeni Değer Ekle"
          size="md"
          :singleItemTextLength="80"
          :shouldShowEmptyIcon="true"
          :hasError="!isCategoryValid"
          customValuePlaceholder="Yeni Değer ekle"
        />
        <div v-if="!isCategoryValid" class="basic-input-icon">
          <TooltipPlugin :tooltipText="inputsErrorText.category">
            <template #tooltip-btn>
              <img src="@/assets/images/icons/alert-circle.svg" />
            </template>
          </TooltipPlugin>
        </div>
      </div>
      <div class="input-with-info d-flex flex-column">
        <DropdownSelectable
          :dropItems="brandSearchResults !== null ? brandSearchResults : brands"
          :key="selectedCategoryId"
          label="Marka*"
          notFoundText="Marka bulunamadı."
          @selectedRadioItem="selectBrand"
          ref="dropdownSelectableBrandsRef"
          isComboBox
          customInputLabel="Yeni Değer Ekle"
          size="md"
          :singleItemTextLength="80"
          :hasError="isBrandError"
          :isCustomValue="hasBrandCustomValue"
          :shouldShowEmptyIcon="true"
          :isSearchBackend="isBrandSearchable"
          :searchDelay="isBrandSearchable ? 0 : 500"
          @changeSearchValue="handleBrandSearch"
        />
        <div v-if="isBrandError" class="basic-input-icon">
          <TooltipPlugin :tooltipText="inputsErrorText.brand">
            <template #tooltip-btn>
              <img src="@/assets/images/icons/alert-circle.svg" />
            </template>
          </TooltipPlugin>
        </div>
      </div>
      <div
        class="input-with-info d-flex flex-column"
        v-if="shouldViewModelCode"
      >
        <Input
          id="linkInput"
          class="link-input"
          placeholder="Model Kodu*"
          v-model:value="modelCode"
          @change="validateModelCode"
          ref="linkInput"
          :isError="!isModelCodeValid"
          @focus="isModelCodeValid = true"
        />
        <div v-if="!isModelCodeValid" class="basic-input-icon">
          <TooltipPlugin :tooltipText="inputsErrorText.modelCode">
            <template #tooltip-btn>
              <img src="@/assets/images/icons/alert-circle.svg" />
            </template>
          </TooltipPlugin>
        </div>
        <span
          >Belirlenmiş bir model kodunuz varsa lütfen bu kodu girin, yoksa renk
          ve ölçü bilgisi sunduğunuz ürünler için kendiniz oluşturun. (Örn:
          TSHRT-MAV-398)
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  onMounted,
  defineEmits,
  defineExpose,
  watch,
  computed,
} from "vue";
import { useStore } from "vuex";
import PageTop from "@/components/Page/PageTop.vue";
import {
  DropdownSelectable,
  Input,
  TooltipPlugin,
} from "@n11/so-ui-components";

const store = useStore();

const emit = defineEmits(["fetchCategoryAttributes"]);
const productName = ref("");
const modelCode = ref("");
const isProductNameValid = ref(true);
const isCategoryValid = ref(true);
const isBrandValid = ref(true);
const isModelCodeValid = ref(true);
const shouldViewModelCode = ref(false);
const hasBrandCustomValue = ref(false);
const categories = ref([]);
const brands = ref([]);
const selectedCategoryId = ref("");
const selectedBrandName = ref("");
const linkInput = ref("");
const brandSearchResults = ref(null);
const brandAttribute = ref(null);
const isBrandSearchable = ref(true);
const inputsErrorText = ref({
  productName: "Ürün Adı zorunludur.",
  modelCode: "Model Kodu zorunludur.",
  category: null,
  brand: null,
});
const dropdownSelectableBrandsRef = ref(null);

const isBrandError = computed(() => {
  return (
    Array.isArray(brands.value) &&
    brands.value.length > 0 &&
    !isBrandValid.value
  );
});

watch(selectedCategoryId, () => {
  isProductNameValid.value = true;
  isBrandValid.value = true;
  isCategoryValid.value = true;
  isModelCodeValid.value = true;
});

const getCategories = async () => {
  try {
    const response = await store.dispatch("categories/getHierarchyCategories");
    categories.value = response.map((category) => ({
      id: category.id,
      name: category.value,
    }));
  } catch (error) {
    console.error("Kategoriler yüklenemedi:", error);
    return [];
  }
};

onMounted(async () => {
  await getCategories();
});

const fetchCategoryAttributes = async (id) => {
  if (!id) {
    brands.value = [];
    brandAttribute.value = null;
    isBrandSearchable.value = false;
    hasBrandCustomValue.value = false;
    shouldViewModelCode.value = false;
    emit("fetchCategoryAttributes", { id: null, categoryAttributes: [] });
    return;
  }
  
  try {
    const response = await store.dispatch(
      "categories/getCategoryAttributes",
      id
    );
    const category = response.categoryAttributes.find(
      (attr) => attr.attributeId === 1 && attr.attributeName === "Marka"
    );
    shouldViewModelCode.value = response.categoryAttributes.some(
      (att) => att.isN11Grouping
    );

    if (!category) {
      console.warn("Marka attribute not found");
      brands.value = [];
      brandAttribute.value = null;
      isBrandSearchable.value = false;
      hasBrandCustomValue.value = false;
    } else {
      brandAttribute.value = category;
      isBrandSearchable.value = !!category.searchable;
      hasBrandCustomValue.value = category.isCustomValue;

      brands.value =
        category.attributeValues?.map(({ id, value }) => ({ id, name: value })) ||
        [];
      brandSearchResults.value = null;
    }

    emit("fetchCategoryAttributes", {
      id,
      categoryAttributes: response.categoryAttributes,
    });
  } catch (error) {
    console.error("Error fetching category attributes:", error);

    brands.value = [];
    brandAttribute.value = null;
    isBrandSearchable.value = false;
    hasBrandCustomValue.value = false;
  }
};

const validateProductName = () => {
  isProductNameValid.value = productName.value.length > 0;
  return isProductNameValid.value;
};

const validateModelCode = () => {
  if (!shouldViewModelCode.value) return true;
  isModelCodeValid.value = modelCode.value.length > 0;
  return isModelCodeValid.value;
};

const validateCategory = () => {
  if (selectedCategoryId.value === "") {
    isCategoryValid.value = false;
    inputsErrorText.value.category = "Kategori seçimi zorunludur.";
    return false;
  }
  isCategoryValid.value = true;
  inputsErrorText.value.category = null;
  return true;
};

const validateBrand = () => {
  if (selectedBrandName.value === "") {
    isBrandValid.value = false;
    inputsErrorText.value.brand = "Marka zorunludur.";
    return false;
  }
  isBrandValid.value = true;
  inputsErrorText.value.brand = null;
  return true;
};

const selectCategory = async (e) => {
  selectedCategoryId.value = e?.id ?? "";
  selectedBrandName.value = "";

  brands.value = [];
  brandSearchResults.value = null;
  brandAttribute.value = null;
  isBrandSearchable.value = false;
  hasBrandCustomValue.value = false;
  dropdownSelectableBrandsRef.value?.clearSelection(); // Dropdown'ın görselini temizle

  try {
    store.commit("common/SET_UPLOAD_IMAGE_SUCCESS", false);
  } catch (error) {
    console.warn('SET_UPLOAD_IMAGE_SUCCESS mutation not found:', error);
  }
  validateCategory();
  await fetchCategoryAttributes(selectedCategoryId.value);
};

const selectBrand = (e) => {
  selectedBrandName.value = e?.name ?? "";
  validateBrand();
};

const handleBrandSearch = async (newValue) => {
  const attr = brandAttribute.value;
  
  if (!attr || !isBrandSearchable.value) {
    brandSearchResults.value = null;
    return;
  }

  try {
    if (!newValue || newValue.trim() === '') {
      brandSearchResults.value = null;
      return;
    }

    const searchResult = await store.dispatch("categories/searchAttributeValue", { 
      attributeId: attr.attributeId,
      categoryId: attr.categoryId,
      value: newValue 
    });
    
    const searchItems = searchResult.data.valueContents?.map(({value, valueId}) => ({id: valueId, name: value}));
    brandSearchResults.value = searchItems || [];
  }
  catch(err) {
    console.warn(err);
    brandSearchResults.value = null; 
  }
}

const getProductInformation = () => {
  const isNameValid = validateProductName();
  const isModelValid = validateModelCode();
  const categoryIdIsValid = validateCategory();
  const brandIdIsValid = validateBrand();
  return {
    data: {
      title: productName?.value,
      categoryId: selectedCategoryId?.value,
      brandId: selectedBrandName?.value,
      productMainId: modelCode?.value,
    },
    isValid: isNameValid && categoryIdIsValid && brandIdIsValid && isModelValid,
  };
};

defineExpose({
  getProductInformation,
});
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/utilities.scss";

.main-area {
  width: 100%;
  display: flex;
  gap: 16px;
  padding: 24px;
  border: 1px solid #d3d6df;
  border-radius: 8px;

  &.without-category {
    background: $colorD20 !important;
  }

  .input-with-info {
    gap: 4px;
    position: relative;

    span {
      font-size: 11px;
      line-height: 14px;
      color: $colorN60;
      padding: 0 12px;
    }

    .basic-input-icon {
      position: absolute;
      z-index: 1;
      right: 0px;
      top: 11px;
      cursor: pointer;

      img {
        width: 18px;
        height: 18px;
      }
    }
  }

  .input-area {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;

    & > div {
      height: max-content;
    }

    .input-with-title {
      gap: 12px;
      font-size: 14px;
      line-height: 18px;
      font-weight: 600;
      color: $colorN80;
    }
  }
}

.flex-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
  margin-bottom: 24px;

  .page-title {
    font-size: 20px;
    line-height: 26px;
    font-weight: 700;
    color: $colorN80;
  }

  .page-description {
    font-size: 14px;
    line-height: 18px;
    font-weight: 400;
    color: $colorN80;

    span {
      font-weight: 600;
    }
  }
}
</style>
